import {ChangeEvent, useEffect, useState} from "react";
import {DatePicker, Input} from "antd";
import classNames from "classnames/bind";
import dayjs from "dayjs";
import Swal from "sweetalert2";

import Button from "~/components/button";
import {MergeGridItem} from "~/components/merge/merge-grid-item";
import {SideModal} from "~/components/modal/side-modal";
import {PageTitle} from "~/components/page-title";
import {ContactEditType, ContactListDataType} from "~/data/board/contact/use-contact.interface";
import {useContactHandler} from "~/data/board/contact/use-contact-list";
import {useUser} from "~/data/user";

import styles from "./contact-board.modal.module.scss";

const cx = classNames.bind(styles);

const ContactBoardModal = ({
    close,
    data,
    refresh,
}: {
    close: () => void;
    data: ContactListDataType | null;
    refresh: () => void;
}) => {
    const [inputs, setInputs] = useState<ContactEditType>({
        contact: "",
        contractPeriod: null,
        contractor: "",
        category: "",
        progressDetail: "",
        relatedProcess: "",
        remark: "",
    });

    const {user} = useUser();

    const {register, update, deleteContact, loading} = useContactHandler({
        id: data?.id,
        contact: inputs.contact,
        relatedProcess: inputs.relatedProcess,
        progressDetail: inputs.progressDetail,
        category: inputs.category,
        contractor: inputs.contractor,
        remark: inputs.remark,
        contractPeriod: inputs.contractPeriod
            ? inputs.contractPeriod[0].format("YY.MM.DD") + " ~ " + inputs.contractPeriod[1].format("YY.MM.DD")
            : "",
    });

    const handleConfirm = () => {
        if (data?.id)
            Swal.fire({
                text: "수정하시겠습니까?",
                showCancelButton: true,
                confirmButtonText: "수정하기",
                cancelButtonText: "취소하기",
            }).then((info) => {
                if (info.isConfirmed) {
                    update();
                    setTimeout(refresh, 500);
                    close();
                }
            });
        else
            Swal.fire({
                text: "등록하시겠습니까?",
                showCancelButton: true,
                confirmButtonText: "등록하기",
                cancelButtonText: "취소하기",
            }).then((info) => {
                if (info.isConfirmed) {
                    register();
                    setTimeout(refresh, 500);
                    close();
                }
            });
    };
    const handleChange = (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => {
        setInputs((p) => ({...p, [e.target.name]: e.target.value}));
    };
    const handleDelete = async () => {
        if (data?.id)
            Swal.fire({
                text: "삭제하시겠습니까?",
                showCancelButton: true,
                confirmButtonText: "삭제하기",
                cancelButtonText: "취소하기",
            }).then((info) => {
                if (info.isConfirmed) {
                    deleteContact(data?.id);
                    setTimeout(refresh, 500);
                    close();
                }
            });
        else
            Swal.fire({
                text: "오류가 발생하였습니다. 다시 시도해주세요",
                confirmButtonText: "확인",
            });
    };

    useEffect(() => {
        if (data)
            setInputs({
                contractPeriod: data.contractPeriod
                    ? [
                          dayjs(data.contractPeriod.split(" ~ ")[0], "YY.MM.DD"),
                          dayjs(data.contractPeriod.split(" ~ ")[1], "YY.MM.DD"),
                      ]
                    : null,
                remark: data.remark,
                contractor: data.contractor,
                contact: data.contact,
                relatedProcess: data.relatedProcess,
                progressDetail: data.progressDetail,
                category: data.category,
            });
    }, [data]);

    return (
        <SideModal
            onClose={close}
            confirmLabel={user?.division === "MEMBER" ? (data ? "수정하기" : "등록하기") : undefined}
            onConfirm={handleConfirm}
            extraButton={
                data && user?.division === "MEMBER"
                    ? [<Button key={"dlt"} label={"삭제하기"} onClick={handleDelete} />]
                    : undefined
            }
        >
            <>
                <PageTitle>업체연락망 {data ? "수정" : "등록"}</PageTitle>
                <div className={cx("form-container")}>
                    <div className={cx("content-container")}>
                        <MergeGridItem name={"구분"} span>
                            {user?.division === "MEMBER" ? (
                                <Input
                                    placeholder={"구분 입력"}
                                    name={"category"}
                                    value={inputs.category}
                                    onChange={handleChange}
                                    disabled={user?.division !== "MEMBER"}
                                />
                            ) : (
                                data?.category
                            )}
                        </MergeGridItem>
                        <MergeGridItem name={"관련공정"} span>
                            {user?.division === "MEMBER" ? (
                                <Input
                                    placeholder={"관련공정 입력"}
                                    name={"relatedProcess"}
                                    value={inputs.relatedProcess}
                                    onChange={handleChange}
                                    disabled={user?.division !== "MEMBER"}
                                />
                            ) : (
                                data?.relatedProcess
                            )}
                        </MergeGridItem>
                        <MergeGridItem name={"계약업체"} span>
                            {user?.division === "MEMBER" ? (
                                <Input
                                    placeholder={"계약업체 입력"}
                                    name={"contractor"}
                                    value={inputs.contractor}
                                    onChange={handleChange}
                                    disabled={user?.division !== "MEMBER"}
                                />
                            ) : (
                                data?.contractor
                            )}
                        </MergeGridItem>
                        <MergeGridItem name={"연락처"}>
                            {user?.division === "MEMBER" ? (
                                <Input
                                    placeholder={"연락처"}
                                    name={"contact"}
                                    value={inputs.contact}
                                    onChange={handleChange}
                                    disabled={user?.division !== "MEMBER"}
                                />
                            ) : (
                                data?.contact
                            )}
                        </MergeGridItem>
                        <MergeGridItem name={"계약기간"}>
                            {user?.division === "MEMBER" ? (
                                <DatePicker.RangePicker
                                    value={inputs.contractPeriod}
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore
                                    onChange={(e) => setInputs((p) => ({...p, contractPeriod: e}))}
                                    className={cx("date-picker")}
                                    disabled={user?.division !== "MEMBER"}
                                />
                            ) : (
                                data?.contractPeriod
                            )}
                        </MergeGridItem>
                        <MergeGridItem name={"비고"} span>
                            {user?.division === "MEMBER" ? (
                                <Input.TextArea
                                    placeholder={"비고 입력"}
                                    name={"remark"}
                                    value={inputs.remark}
                                    onChange={handleChange}
                                    disabled={user?.division !== "MEMBER"}
                                />
                            ) : (
                                <pre>{data?.remark}</pre>
                            )}
                        </MergeGridItem>
                        <MergeGridItem name={"세부 진행사항"} span>
                            {user?.division === "MEMBER" ? (
                                <Input.TextArea
                                    placeholder={"세부 진행사항 입력"}
                                    name={"progressDetail"}
                                    value={inputs.progressDetail}
                                    onChange={handleChange}
                                    disabled={user?.division !== "MEMBER"}
                                />
                            ) : (
                                <pre>{data?.progressDetail}</pre>
                            )}
                        </MergeGridItem>
                    </div>
                </div>
            </>
        </SideModal>
    );
};

export {ContactBoardModal};
